import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import s from "./RolePlay.module.css";
import { useAiChat } from "../AiChatContext";
import { AiSparkleIcon } from "../components/AiChatPopup";
import FeedbackCard from "../components/FeedbackCard";
import { apiFetch } from "../../../../api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════
   Speech Recognition hook
   ═══════════════════════════════════ */

const SpeechRecognition = typeof window !== "undefined"
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : null;

function useSpeechRecognition() {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef(null);

  const supported = !!SpeechRecognition;

  const startListening = useCallback(() => {
    if (!SpeechRecognition) return;
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "sv-SE";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onresult = (event) => {
      const result = event.results[0]?.[0]?.transcript || "";
      setTranscript(result);
      setListening(false);
    };

    recognition.onerror = () => {
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognitionRef.current = recognition;
    setTranscript("");
    setListening(true);
    recognition.start();
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript("");
  }, []);

  const reset = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setListening(false);
    setTranscript("");
  }, []);

  useEffect(() => () => {
    if (recognitionRef.current) recognitionRef.current.abort();
  }, []);

  return { listening, transcript, supported, startListening, stopListening, clearTranscript, reset };
}

/* ═══════════════════════════════════
   Word comparison logic
   ═══════════════════════════════════ */

function stripWord(w) {
  return w.replace(/[^\p{L}\p{N}]/gu, "").toLowerCase();
}

function compareWords(correctText, userText) {
  const correctWords = correctText.split(/\s+/).filter(Boolean);
  const userWords = userText.split(/\s+/).filter(Boolean);
  const userStripped = userWords.map(stripWord);

  const results = correctWords.map((word) => {
    const target = stripWord(word);
    if (!target) return { word, correct: true };
    const found = userStripped.includes(target);
    return { word, correct: found };
  });

  const correctCount = results.filter((r) => r.correct).length;
  const incorrectCount = results.filter((r) => !r.correct).length;
  const display = results.map((r) => (r.correct ? r.word : "...")).join(" ");
  const total = results.filter((r) => stripWord(r.word)).length;
  const allCorrect = total > 0 && correctCount / total >= 0.7;

  return { results, display, correctCount, incorrectCount, allCorrect };
}

const ENCOURAGEMENTS = [
  "Excellent pronunciation!",
  "You nailed it!",
  "Fantastiskt! (Fantastic!)",
  "Bra jobbat! (Great job!)",
  "Perfect! Keep going!",
  "Spot on!",
];

function getEncouragement() {
  return ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)];
}

/* ═══════════════════════════════════
   SVG Icons
   ═══════════════════════════════════ */

function MicIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
    </svg>
  );
}

function PlayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
    </svg>
  );
}

function StopIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

function SpeakerIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
    </svg>
  );
}

/* ═══════════════════════════════════
   Main Component
   ═══════════════════════════════════ */

export default function RolePlay({ onBack, onAskAi, storyId, moduleId, onComplete, reviewMode = false }) {
  const { setModuleContext } = useAiChat();
  const [storyData, setStoryData] = useState(null);
  const [moduleStatus, setModuleStatus] = useState("idle"); // idle | playing | feedback
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isCompleting, setIsCompleting] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [checkResult, setCheckResult] = useState(null);
  const [showSwedish, setShowSwedish] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [encouragement, setEncouragement] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [sessionStats, setSessionStats] = useState([]); // per-card stats

  const { listening, transcript, supported, startListening, stopListening, clearTranscript, reset } = useSpeechRecognition();
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);

  const sentences = useMemo(() => storyData?.sentences || [], [storyData]);
  const totalSteps = sentences.length + 1;
  const currentStep = currentIndex + 1;
  const isDone = currentIndex === sentences.length;

  const currentData = useMemo(() => {
    if (currentIndex === -1) {
      return {
        swedish: storyData?.topic?.in_swedish || "",
        english: storyData?.topic?.in_english || "",
        audioPath: "topic/topic_in_swedish.mp3",
      };
    }
    if (currentIndex >= 0 && currentIndex < sentences.length) {
      const sent = sentences[currentIndex];
      return {
        swedish: sent.in_swedish || "",
        english: sent.in_english || "",
        audioPath: `sentences/${sent.id}/sentence_in_swedish.mp3`,
      };
    }
    return null;
  }, [currentIndex, sentences, storyData]);

  // Report AI chat context
  useEffect(() => {
    if (!currentData) return;
    const ctx = {
      origin: reviewMode ? "daily_practice_role_play" : "role_play",
      story_topic: storyData?.topic ? { in_swedish: storyData.topic.in_swedish, in_english: storyData.topic.in_english } : undefined,
      sentence: { in_swedish: currentData.swedish, in_english: currentData.english },
    };
    if (transcript) ctx.user_speech_transcript = transcript;
    if (checkResult) ctx.speech_result = { allCorrect: checkResult.allCorrect, display: checkResult.display };
    setModuleContext(ctx);
  }, [currentData, transcript, checkResult, storyData, setModuleContext, reviewMode]);

  // Navigate between cards
  const navigateTo = useCallback((newIndex, direction) => {
    if (animating) return;
    reset();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setAudioPlaying(false);
    setSlideDirection(direction);
    setAnimating(true);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setCheckResult(null);
      setShowSwedish(false);
      setAnimating(false);
      setAttempts(0);
      setEncouragement("");
    }, 200);
  }, [animating, reset]);

  const goNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex <= sentences.length) {
      // Save stat for current card
      setSessionStats(prev => {
        const updated = [...prev];
        if (!updated[currentStep]) {
          updated[currentStep] = {
            correct: checkResult?.allCorrect || false,
            attempts: attempts,
          };
        }
        return updated;
      });
      navigateTo(nextIndex, "left");
    }
  }, [currentIndex, sentences.length, navigateTo, currentStep, checkResult, attempts]);

  // Toggle microphone
  const toggleMic = useCallback(() => {
    if (listening) {
      stopListening();
    } else {
      setAttempts(prev => prev + 1);
      startListening();
    }
  }, [listening, startListening, stopListening]);

  // Play original sentence audio
  const playOriginal = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (!currentData) return;
    const url = `${BACKEND}/api/v1/admin/audio/${storyId}/${currentData.audioPath}`;
    const audio = new Audio(url);
    setAudioPlaying(true);
    audio.onended = () => { setAudioPlaying(false); audioRef.current = null; };
    audio.onerror = () => { setAudioPlaying(false); audioRef.current = null; };
    audio.play().catch(() => { setAudioPlaying(false); });
    audioRef.current = audio;
  }, [currentData, storyId]);

  // Auto-check when transcript arrives
  useEffect(() => {
    if (!transcript || !currentData) return;
    const result = compareWords(currentData.swedish, transcript);
    setCheckResult(result);

    if (result.allCorrect) {
      setEncouragement(getEncouragement());
    } else {
      setEncouragement("");
      setTimeout(() => {
        setCheckResult(null);
        clearTranscript();
      }, 4000);
    }
  }, [transcript, currentData, clearTranscript]);

  // Cleanup on unmount
  useEffect(() => () => {
    reset();
    if (audioRef.current) audioRef.current.pause();
  }, [reset]);

  // Start module — fetch story data from /start
  const handleStart = useCallback(async () => {
    if (!storyId) { setStartError("No story selected"); return; }
    if (!reviewMode && !moduleId) { setModuleStatus("playing"); return; }
    setIsStarting(true);
    setStartError(null);
    try {
      const url = reviewMode
        ? `/api/v1/daily-practice/stories/${storyId}/start`
        : `/api/v1/dashboard/modules/${storyId}/${moduleId}/start`;
      const method = reviewMode ? "POST" : "PATCH";
      const res = await apiFetch(url, { method });
      if (!res.ok) throw new Error("Could not start module");
      const data = await res.json();
      setStoryData({
        story_id: data.story_id,
        topic: data.topic,
        sentences: data.sentences,
      });
      setModuleStatus("playing");
    } catch (err) {
      setStartError(err.message);
    } finally {
      setIsStarting(false);
    }
  }, [storyId, moduleId, reviewMode]);

  // Auto-complete when done screen appears
  const completeCalled = useRef(false);
  useEffect(() => {
    if (!isDone || completeCalled.current || !storyId) return;
    if (!reviewMode && !moduleId) return;
    completeCalled.current = true;
    setIsCompleting(true);
    (async () => {
      try {
        const url = reviewMode
          ? `/api/v1/daily-practice/stories/${storyId}/complete`
          : `/api/v1/dashboard/modules/${storyId}/${moduleId}/complete`;
        const method = reviewMode ? "POST" : "PATCH";
        const res = await apiFetch(url, { method });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setIsCompleting(false);
        if (reviewMode) {
          onComplete?.(data);
          onBack();
        } else if (!data.feeling) {
          setModuleStatus("feedback");
        } else {
          onComplete?.();
          onBack();
        }
      } catch {
        setIsCompleting(false);
        if (reviewMode) {
          onComplete?.();
          onBack();
        } else {
          setModuleStatus("feedback");
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone, storyId, moduleId, reviewMode]);

  const handleClose = useCallback(async () => {
    if (!storyId || !moduleId) { onBack(); return; }
    setIsCompleting(true);
    try {
      const res = await apiFetch(`/api/v1/dashboard/modules/${storyId}/${moduleId}/complete`, { method: "PATCH" });
      if (!res.ok) throw new Error("Could not mark module complete");
      const data = await res.json();
      setIsCompleting(false);
      if (data.feeling) {
        onComplete?.();
        onBack();
      } else {
        setModuleStatus("feedback");
      }
    } catch {
      setIsCompleting(false);
      setModuleStatus("feedback");
    }
  }, [storyId, moduleId, onComplete, onBack]);

  const handleFeedbackDone = useCallback(() => {
    onComplete?.();
    onBack();
  }, [onComplete, onBack]);

  const slideClass = animating
    ? (slideDirection === "left" ? s.slideOutLeft : s.slideOutRight)
    : (slideDirection ? (slideDirection === "left" ? s.slideInRight : s.slideInLeft) : s.slideInInitial);

  const isOnTopic = currentIndex === -1;

  // Stats for done screen
  const correctCards = sessionStats.filter(st => st?.correct).length;
  const totalCards = totalSteps;

  // ── Render: idle start screen ──
  if (moduleStatus === "idle") {
    return (
      <div className={s.panel}>
        <div className={s.header}>
          <button className={s.backBtn} onClick={onBack} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          {onAskAi && (
            <button className={s.headerAiBtn} onClick={onAskAi} aria-label="AI tutor">
              <AiSparkleIcon className={s.headerAiBtnIcon} />
              <span className={s.headerAiBtnLabel}>AI</span>
            </button>
          )}
        </div>
        <div className={s.content}>
          <div className={s.startScreen}>
            {/* Hero icon */}
            <div className={s.startHero}>
              <div className={s.startIconRing}>
                <div className={s.startIconInner}>
                  <MicIcon className={s.startIconSvg} />
                </div>
              </div>
              <h3 className={s.startTitle}>Speak Swedish</h3>
              <button className={s.startBtn} onClick={handleStart} disabled={isStarting}>
                <PlayIcon className={s.startBtnIcon} />
                <span>{isStarting ? "Starting..." : "Start Practice"}</span>
              </button>
              {startError && <p className={s.startError}>{startError}</p>}
            </div>

            {/* How it works */}
            <div className={s.howItWorks}>
              <div className={s.howStep}>
                <span className={s.howStepNum}>1</span>
                <div className={s.howStepText}>
                  <span className={s.howStepTitle}>Read the English</span>
                  <span className={s.howStepDesc}>Understand what you need to say</span>
                </div>
              </div>
              <div className={s.howStep}>
                <span className={s.howStepNum}>2</span>
                <div className={s.howStepText}>
                  <span className={s.howStepTitle}>Speak it out</span>
                  <span className={s.howStepDesc}>Record yourself saying the Swedish sentence</span>
                </div>
              </div>
              <div className={s.howStep}>
                <span className={s.howStepNum}>3</span>
                <div className={s.howStepText}>
                  <span className={s.howStepTitle}>Listen first</span>
                  <span className={s.howStepDesc}>Need a hint? Hear the correct pronunciation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: feedback ──
  if (moduleStatus === "feedback") {
    return (
      <div className={s.panel}>
        <div className={s.header}>
          <button className={s.backBtn} onClick={handleFeedbackDone} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          {onAskAi && (
            <button className={s.headerAiBtn} onClick={onAskAi} aria-label="AI tutor">
              <AiSparkleIcon className={s.headerAiBtnIcon} />
              <span className={s.headerAiBtnLabel}>AI</span>
            </button>
          )}
        </div>
        <div className={s.content}>
          <div className={s.doneScreen}>
            <h3 className={s.doneTitle}>Well Done!</h3>
            <p className={s.doneDesc}>You've completed the role play practice.</p>
            <FeedbackCard storyId={storyId} moduleId={moduleId} onDone={handleFeedbackDone} />
          </div>
        </div>
      </div>
    );
  }

  // Browser not supported
  if (!supported) {
    return (
      <div className={s.panel}>
        <div className={s.header}>
          <button className={s.backBtn} onClick={onBack} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          {onAskAi && (
            <button className={s.headerAiBtn} onClick={onAskAi} aria-label="AI tutor">
              <AiSparkleIcon className={s.headerAiBtnIcon} />
              <span className={s.headerAiBtnLabel}>AI</span>
            </button>
          )}
        </div>
        <div className={s.content}>
          <div className={s.unsupported}>
            <MicIcon className={s.unsupportedIcon} />
            <h3 className={s.unsupportedTitle}>Speech Recognition Not Supported</h3>
            <p className={s.unsupportedDesc}>
              Your browser doesn't support speech recognition. Please use <strong>Chrome</strong>, <strong>Edge</strong>, or <strong>Safari</strong>.
            </p>
            <button className={s.doneCloseBtn} onClick={onBack}>
              <span>Go Back</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.panel}>
      {/* Top Bar — X + progress (like VocabReview / StoryRecap) */}
      <div className={s.topBar}>
        <button className={s.closeBtn} onClick={handleClose} disabled={isCompleting} title="Back to modules">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        {!isDone && (
          <div className={s.topProgressBar}>
            <div className={s.topProgressFill} style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }} />
          </div>
        )}
        {onAskAi && (
          <button className={s.headerAiBtn} onClick={onAskAi} aria-label="AI tutor">
            <AiSparkleIcon className={s.headerAiBtnIcon} />
            <span className={s.headerAiBtnLabel}>AI</span>
          </button>
        )}
      </div>

      {/* Card Content */}
      <div className={s.content}>
        {isDone ? (
          <div className={s.doneScreen}>
            <div className={s.confettiWrap}>
              {Array.from({ length: 18 }, (_, i) => (
                <div key={i} className={s.confettiPiece} style={{
                  '--x': `${(Math.random() - 0.5) * 280}px`,
                  '--y': `${-80 - Math.random() * 60}px`,
                  '--r': `${Math.random() * 540}deg`,
                  '--d': `${0.1 + Math.random() * 0.4}s`,
                  '--c': ['#9b72cf', '#d4a843', '#5b9bd5', '#40916c', '#7c5cb0', '#e8c560', '#e07858', '#818cf8'][i % 8],
                }} />
              ))}
            </div>
            <div className={s.doneStars}>
              <svg className={s.doneStar} viewBox="0 0 24 24" fill="currentColor" style={{ animationDelay: '0.4s' }}><path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg>
              <svg className={s.doneStar} viewBox="0 0 24 24" fill="currentColor" style={{ animationDelay: '0.55s' }}><path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg>
              <svg className={s.doneStar} viewBox="0 0 24 24" fill="currentColor" style={{ animationDelay: '0.7s' }}><path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg>
            </div>
            <div className={s.doneIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <h3 className={s.doneTitle}>Well Done!</h3>
            <p className={s.doneDesc}>You've completed the role play practice.</p>

            {/* Session stats */}
            <div className={s.doneStats}>
              <div className={s.doneStat}>
                <span className={s.doneStatValue}>{totalCards}</span>
                <span className={s.doneStatLabel}>Cards</span>
              </div>
              <div className={s.doneStatDivider} />
              <div className={s.doneStat}>
                <span className={`${s.doneStatValue} ${s.doneStatCorrect}`}>{correctCards}</span>
                <span className={s.doneStatLabel}>Correct</span>
              </div>
              <div className={s.doneStatDivider} />
              <div className={s.doneStat}>
                <span className={s.doneStatValue}>{totalCards > 0 ? Math.round((correctCards / totalCards) * 100) : 0}%</span>
                <span className={s.doneStatLabel}>Accuracy</span>
              </div>
            </div>

            <button className={s.doneCloseBtn} onClick={() => { onComplete?.(); onBack(); }}>
              <span>Close</span>
            </button>
          </div>
        ) : (
          <div className={`${s.card} ${slideClass}`} key={currentIndex}>
           <div className={s.cardInner}>
            {/* Badge */}
            <div className={s.cardBadge}>
              {isOnTopic ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                  </svg>
                  <span>Story Topic</span>
                </>
              ) : (
                <span className={s.sentenceNum}>{String(currentIndex + 1).padStart(2, "0")}</span>
              )}
            </div>

            {/* English translation (always visible) */}
            <p className={s.translationText}>{currentData.english}</p>

            {/* Action buttons row: Listen + Reveal */}
            <div className={s.actionRow}>
              <button
                className={`${s.listenBtn} ${audioPlaying ? s.listenBtnActive : ""}`}
                onClick={playOriginal}
                title="Listen to the Swedish pronunciation"
              >
                <SpeakerIcon className={s.listenBtnIcon} />
                <span>{audioPlaying ? "Playing..." : "Listen first"}</span>
                {audioPlaying && (
                  <span className={s.soundWave}>
                    <span className={s.soundBar} />
                    <span className={s.soundBar} />
                    <span className={s.soundBar} />
                    <span className={s.soundBar} />
                  </span>
                )}
              </button>
              <button
                className={`${s.revealBtn} ${showSwedish ? s.revealBtnActive : ""}`}
                onClick={() => setShowSwedish((v) => !v)}
                title={showSwedish ? "Hide Swedish" : "Show Swedish"}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  {showSwedish ? (
                    <path d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                  ) : (
                    <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  )}
                </svg>
                <span>{showSwedish ? "Hide" : "Peek"}</span>
              </button>
            </div>

            {/* Swedish reveal */}
            {showSwedish && (
              <div className={s.swedishReveal}>
                <p className={s.swedishText}>{currentData.swedish}</p>
              </div>
            )}

            {/* Microphone area — the main interaction */}
            <div className={s.micArea}>
              <button
                className={`${s.micBtn} ${listening ? s.micBtnActive : ""} ${checkResult?.allCorrect ? s.micBtnSuccess : ""}`}
                onClick={toggleMic}
                disabled={checkResult?.allCorrect}
              >
                {listening ? (
                  <>
                    <div className={s.micPulseRing} />
                    <StopIcon className={s.micBtnIcon} />
                  </>
                ) : checkResult?.allCorrect ? (
                  <svg className={s.micBtnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                ) : (
                  <MicIcon className={s.micBtnIcon} />
                )}
              </button>
              <div className={s.micLabel}>
                {listening ? (
                  <span className={s.listeningLabel}>
                    <span className={s.listeningDot} />
                    Listening...
                  </span>
                ) : checkResult?.allCorrect ? (
                  <span className={s.successLabel}>{encouragement}</span>
                ) : (
                  <span className={s.micHint}>
                    {attempts === 0 ? "Tap to speak" : "Try again"}
                  </span>
                )}
              </div>
            </div>

            {/* Transcribed text + word-level feedback */}
            {transcript && checkResult && (
              <div className={`${s.feedbackCard} ${checkResult.allCorrect ? s.feedbackCardCorrect : s.feedbackCardIncorrect}`}>
                <div className={s.feedbackHeader}>
                  <span className={s.feedbackIcon}>
                    {checkResult.allCorrect ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="12" cy="12" r="10" /><path d="M12 8v4m0 4h.01" />
                      </svg>
                    )}
                  </span>
                  <span className={s.feedbackTitle}>
                    {checkResult.allCorrect ? "Great match!" : "Almost there"}
                  </span>
                  <span className={s.feedbackScore}>
                    {checkResult.correctCount}/{checkResult.correctCount + checkResult.incorrectCount}
                  </span>
                </div>

                {/* Word-by-word colored display */}
                <div className={s.wordResults}>
                  {checkResult.results.map((r, i) => (
                    <span key={i} className={`${s.wordChip} ${r.correct ? s.wordCorrect : s.wordMissed}`}>
                      {r.correct ? r.word : r.word}
                    </span>
                  ))}
                </div>

                {/* Your speech transcript */}
                <div className={s.transcriptRow}>
                  <span className={s.transcriptLabel}>You said:</span>
                  <span className={s.transcriptText}>{transcript}</span>
                </div>

                {/* Actions after feedback */}
                {checkResult.allCorrect && (
                  <div className={s.feedbackActions}>
                    <button className={s.nextBtn} onClick={goNext}>
                      <span>Next</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Skip button — always available */}
            {!checkResult?.allCorrect && !listening && (
              <button className={s.skipBtn} onClick={goNext}>
                <span>Skip this one</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
           </div>
          </div>
        )}
      </div>

    </div>
  );
}
